const PDF_MIME = 'application/pdf'
const FALLBACK_MIME = 'application/octet-stream'
const DEFAULT_FILE_NAME = 'referto'

const MIME_TYPE_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'application/pkcs7-mime': 'p7m',
  'application/x-pkcs7-mime': 'p7m',
  'application/pkcs7-signature': 'p7m',
  'application/x-pkcs7-signature': 'p7m'
}

export interface MedicalReportFile {
  blob: Blob
  fileName: string
  isPdf: boolean
}

export const getFileExtension = (fileName?: string): string => {
  const match = /\.([^./\\]+)$/.exec(fileName || '')
  return match ? match[1].toLowerCase() : ''
}

export const getExtensionFromMimeType = (mimeType?: string): string => {
  const normalizedMimeType = (mimeType || '').split(';')[0].trim().toLowerCase()
  return MIME_TYPE_EXTENSIONS[normalizedMimeType] || ''
}

// Il content type vince sul nome; se manca o è generico decide l'estensione
export const isPdfFile = (fileName?: string, mimeType?: string): boolean => {
  const extensionFromMimeType = getExtensionFromMimeType(mimeType)
  if (extensionFromMimeType) {
    return extensionFromMimeType === 'pdf'
  }
  return getFileExtension(fileName) === 'pdf'
}

export const getFileNameFromContentDisposition = (contentDisposition?: string): string => {
  if (!contentDisposition) {
    return ''
  }
  const encodedMatch = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(contentDisposition)
  if (encodedMatch) {
    try {
      return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ''))
    } catch (error) {
      // un nome codificato male ricade sul parametro filename semplice
    }
  }
  const plainMatch = /filename\s*=\s*("([^"]*)"|[^;]+)/.exec(contentDisposition)
  if (!plainMatch) {
    return ''
  }
  return (plainMatch[2] ?? plainMatch[1]).trim()
}

// Tiene il nome originale e aggiunge l'estensione del content type se il nome non finisce già con quella
export const buildDownloadFileName = (originalName?: string, mimeType?: string): string => {
  const fileName = originalName || DEFAULT_FILE_NAME
  const extension = getExtensionFromMimeType(mimeType)
  if (!extension || getFileExtension(fileName) === extension) {
    return fileName
  }
  return `${fileName}.${extension}`
}

export const buildMedicalReportFile = (data: ArrayBuffer, headers: Record<string, any> = {}): MedicalReportFile => {
  const contentType: string | undefined = headers['content-type']
  const originalName = getFileNameFromContentDisposition(headers['content-disposition'])
  const isPdf = isPdfFile(originalName, contentType)
  return {
    blob: new Blob([new Uint8Array(data)], {type: isPdf ? PDF_MIME : (contentType || FALLBACK_MIME)}),
    fileName: buildDownloadFileName(originalName, isPdf ? PDF_MIME : contentType),
    isPdf
  }
}

export const saveBlob = (blob: Blob, fileName: string) => {
  const url = (window.URL || window.webkitURL).createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  // revocare subito dopo il click può annullare il download in alcuni browser
  setTimeout(() => (window.URL || window.webkitURL).revokeObjectURL(url), 1000)
}
